import React, { useEffect } from 'react';
import Modal from 'Common/Components/Modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';

interface UserModalProps {
  show: boolean;
  isEdit: boolean;
  eventData: any;
  roles: any[];
  isReset?: boolean;
  onHide: () => void;
  onSubmit: (values: any) => void;
}

const UserModal: React.FC<UserModalProps> = ({ show, isEdit, eventData, roles, isReset, onHide, onSubmit }) => {
  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: isEdit
      ? {
        userId: eventData?.id || '',
        userName: eventData?.userName || '',
        firstName: eventData?.name || '',
        lastName: eventData?.surname || '',
        email: eventData?.email || '',
        phoneNumber: eventData?.phoneNumber || '',
        role: eventData?.role || '',
        password: eventData?.password || '',
        isActive: eventData?.isActive || false,
      }
      : {
        userId: '',
        userName: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        role: '',
        isActive: true,
      },
    validationSchema: Yup.object().shape({
      userName: Yup.string().required("Please Enter userName").test(
        'no-whitespace',
        'Username must not contain spaces',
        (value) => !value?.includes(' ')
      ),
      email: Yup.string()
        .email("Invalid email address")
        .required("Please Enter Email"),
      phoneNumber: Yup.string()
        .matches(/^\+\d{11}$/, "Phone number must be 11 digits, starting with + followed by country code and number")
        .notRequired(),
      role: Yup.string().oneOf(['ADMIN', 'STAFF', ''], 'Invalid role selected'),
      isActive: Yup.boolean(),
      password: Yup.string().when([], {
        is: () => isEdit,
        then: () =>
          Yup.string()
            .notRequired()
            .min(8, 'Password must be at least 8 characters')
            .matches(/[^a-zA-Z0-9]/, 'Passwords must have at least one non-alphanumeric character')
            .matches(/[a-z]/, 'Passwords must have at least one lowercase letter (a-z)')
            .matches(/[A-Z]/, 'Passwords must have at least one uppercase letter (A-Z)'),
        otherwise: () =>
          Yup.string()
            .required('Please Enter Password')
            .min(8, 'Password must be at least 8 characters')
            .matches(/[^a-zA-Z0-9]/, 'Passwords must have at least one non-alphanumeric character')
            .matches(/[a-z]/, 'Passwords must have at least one lowercase letter (a-z)')
            .matches(/[A-Z]/, 'Passwords must have at least one uppercase letter (A-Z)'),
      }),
    }),
    onSubmit,
  });

  useEffect(() => {
    if (isReset) {
      validation.resetForm();
    }
  }, [isReset, show]);

  return (
    <Modal
      show={show}
      onHide={(e: any) => {
        onHide();
      }}
      id="defaultModal"
      modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] max-w-[25rem] md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600"
    >
      <Modal.Header
        className="flex items-center justify-between p-4 border-b dark:border-zink-300/20"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
      >
        <Modal.Title className="text-16">{isEdit ? "Edit User" : "Add User"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          {validation.values.userId && (
            <div className="mb-3">
              <label htmlFor="id" className="inline-block mb-2 text-base font-medium">User ID</label>
              <input
                type="text"
                id="id"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                disabled
                value={validation.values.userId}
              />
            </div>
          )}

          <div className="mb-3">
            <label htmlFor="userNameInput" className="inline-block mb-2 text-base font-medium">
              User Name <span className="text-red-500 cursor-pointer" title="This field is mandatory">*</span>
            </label>
            <input
              type="text"
              id="userNameInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Enter name"
              name="userName"
              onChange={validation.handleChange}
              value={validation.values.userName || ""}
              onBlur={validation.handleBlur}
            />
            {validation.touched.userName && validation.errors.userName && (
              <p className="text-red-400">{validation.errors.userName}</p>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="userNameInput" className="inline-block mb-2 text-base font-medium">
              First Name
            </label>
            <input
              type="text"
              id="firstNameInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Enter first name"
              name="firstName"
              onChange={validation.handleChange}
              value={validation.values.firstName || ""}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="userNameInput" className="inline-block mb-2 text-base font-medium">
              Last Name
            </label>
            <input
              type="text"
              id="lastNameInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Enter last name"
              name="lastName"
              onChange={validation.handleChange}
              value={validation.values.lastName || ""}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="emailInput" className="inline-block mb-2 text-base font-medium">
              Email <span className="text-red-500 cursor-pointer" title="This field is mandatory">*</span>
            </label>
            <input
              type="email"
              id="emailInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Enter email"
              name="email"
              onChange={validation.handleChange}
              value={validation.values.email || ""}
              onBlur={validation.handleBlur}
            />
            {validation.touched.email && validation.errors.email && (
              <p className="text-red-400">{validation.errors.email}</p>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="phoneNumberInput" className="inline-block mb-2 text-base font-medium">
              Phone Number
            </label>
            <input
              type="text"
              id="phoneNumberInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="+[country code][number] (e.g., +1234567890)"
              name="phoneNumber"
              onChange={validation.handleChange}
              value={validation.values.phoneNumber || ""}
              onBlur={validation.handleBlur}
            />
            {validation.touched.phoneNumber && validation.errors.phoneNumber && (
              <p className="text-red-400">{validation.errors.phoneNumber}</p>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="roleInput" className="inline-block mb-2 text-base font-medium">Role</label>
            <select
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              name="role"
              value={validation.values.role}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            >
              <option value="">Select role for user</option>
              {roles?.map((role: any) => (
                <option key={role.id} value={role.name.toUpperCase()}>
                  {role.name.toUpperCase()}
                </option>
              ))}
            </select>
            {validation.errors.role && validation.touched.role && (
              <p className="text-red-400">{validation.errors.role}</p>
            )}
          </div>
          <div className="mb-3">
            <label htmlFor="passwordInput" className="inline-block mb-2 text-base font-medium">
              Password {!isEdit && <span className="text-red-500 cursor-pointer" title="This field is mandatory">*</span>}
            </label>
            <input
              type="password"
              id="passwordInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Enter password"
              name="password"
              onChange={validation.handleChange}
              value={validation.values.password || ""}
              onBlur={validation.handleBlur}
            />
            {validation.touched.password && validation.errors.password && (
              <p className="text-red-400">{validation.errors.password}</p>
            )}
          </div>
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActiveCheckbox"
                className="w-4 h-4 border rounded appearance-none bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400"
                name="isActive"
                checked={validation.values.isActive || false}
                onChange={(e) => validation.setFieldValue('isActive', e.target.checked)}
              />
              <label htmlFor="isActiveCheckbox" className="inline-block text-base font-medium">
                Active User
              </label>
            </div>
            {validation.touched.isActive && validation.errors.isActive && (
              <p className="text-red-400">{validation.errors.isActive}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="reset"
              className="bg-white text-slate-500 btn hover:text-slate-500 hover:bg-slate-100 focus:text-slate-500 focus:bg-slate-100 active:text-slate-500 active:bg-slate-100 dark:bg-zink-600 dark:hover:bg-slate-500/10 dark:focus:bg-slate-500/10 dark:active:bg-slate-500/10 transition-all duration-200 ease-linear"
              onClick={(e) => {
                e.preventDefault();
                onHide();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            >
              {isEdit ? "Update User" : "Add User"}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default UserModal;